import React, { type FC, useContext, Fragment } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';
import startCase from 'lodash/startCase';
import kebabCase from 'lodash/kebabCase';

import type { Lucid } from '../../types/types';
import Heading from '../heading/heading';
import Image from '../image/image';
import Paper from '../paper/paper';
import Container from '../container/container';
import Icon from '../icon/icon';
import LayoutContext from '../context/layout-context';
import AccessibleDescription from '../accessible-description/accessible-description';

/**
 * * @params:
 * * * [0]: feature - string - The feature being compared
 * * * [1]: isOnLucid - boolean - Returns true if Lucid product has the feature
 * * * [2]: isOnCompetitor - boolean - Returns true if competitor has the feature
 */
type Feature = [string, boolean, boolean];

export type TableData = {
  /** The category label for the list of features being compared */
  category: string;
  /** Array of Tuples containing all comparison values for the row of table data */
  features: Feature[];
};

export interface ComparisonTableProps {
  /** The competing product being compared to Lucid */
  competitor: string;
  /** The heading to be displayed above the table, outside of the raised Paper component */
  heading?: string;
  // TODO: product could be pulled dynamically from the LayoutContext
  /** The Lucid product being compared */
  product: Lucid.Product.Name | 'lucid';
  /** The data that will populate the table */
  tableData: TableData[];
}

const productLogos: Record<Lucid.Product.Name | 'lucid', string> = {
  lucid: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/Lucid-L.svg',
  lucidspark: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucidspark-icon.svg',
  lucidchart: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucidchart-icon.svg',
  lucidscale: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucidscale-icon.svg',
};

// TODO:
// - MVP did not call for a 4th column, but it is possible we may have to account for this in the future
const ComparisonTable: FC<ComparisonTableProps> = ({ competitor, heading, product, tableData }) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    category: 'competitor',
    shortCode,
  });

  return (
    <Container
      css={theme => ({
        label: 'comparisonTable',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: theme.space.s64,
        paddingBottom: theme.space.s64,
        textAlign: 'center',
        paddingRight: '1rem',
        paddingLeft: '1rem',
        [theme.breakpoints.bp768.min]: {
          paddingRight: '2rem',
          paddingLeft: '2rem',
        },
      })}
    >
      {heading && (
        <Heading
          as="h2"
          css={theme => ({ padding: `0 ${theme.space.s24}px ${theme.space.s8}px`, maxWidth: '680px' })}
          id="comparison_table_description"
          variant="headingXL"
        >
          {heading}
        </Heading>
      )}
      <Paper
        aria-labelledby="comparison_table_description"
        css={theme => ({
          width: '100%',
          maxWidth: '1020px',
          border: `1px solid ${theme.colors.coolGrey[100]}`,
          boxShadow: `10px 10px 0 0 ${theme.colors.coolGrey[10]}`,
          padding: `${theme.space.s64}px ${theme.space.s24}px`,
          [theme.breakpoints.bp768.min]: {
            padding: theme.space.s64,
          },
        })}
        role="region"
        tabIndex={0}
        variant="raisedLow"
      >
        <table
          css={{
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr
              css={theme => ({
                th: {
                  fontSize: theme.fontSizes.fs12,
                  fontWeight: theme.fontWeights.fw500,
                  padding: `0 ${theme.space.s8}px`,
                  textAlign: 'center',
                  [theme.breakpoints.bp500.min]: {
                    fontSize: theme.fontSizes.fs24,
                  },
                },
              })}
            >
              <th scope="col">{withSmartling('mktg_competitor_item_features')}</th>
              <th scope="col">
                <span css={{ display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'center' }}>
                  <Image aria-hidden="true" height="25px" imageAlt="" imageSrc={productLogos[product]} />
                  <span
                    css={theme => ({
                      fontWeight: 500,
                      width: 0,
                      overflow: 'hidden',
                      letterSpacing: '-1px',
                      [theme.breakpoints.bp768.min]: { width: 'unset', overflow: 'visible' },
                    })}
                  >
                    {startCase(product.replace(/\blucid\b/, 'Lucid Suite'))}
                  </span>
                </span>
              </th>
              <th scope="col">{competitor || 'Competitor'}</th>
            </tr>
          </thead>
          <tbody
            css={theme => ({
              td: {
                borderTop: `1px solid ${theme.colors.coolGrey[100]}`,
                borderBottom: `1px solid ${theme.colors.coolGrey[100]}`,
                padding: `${theme.space.s16}px ${theme.space.s8}px`,
                [theme.breakpoints.bp500.min]: {
                  padding: theme.space.s8,
                },
                ['&:not(.feature_column)']: {
                  textAlign: 'center',
                  width: '64px',
                  [theme.breakpoints.bp500.min]: {
                    minWidth: '120px',
                  },
                  [theme.breakpoints.bp768.min]: {
                    width: '25%',
                  },
                },
              },
              th: {
                padding: `${theme.space.s16}px ${theme.space.s8}px`,
              },
            })}
          >
            {tableData.map(({ category, features }) => (
              <Fragment key={category}>
                <tr>
                  <th className="feature_column" scope="row">
                    {withSmartling(`mktg_competitor_item_${kebabCase(category)}`) || category}
                  </th>
                </tr>
                {features.map(([feature, isOnLucid, isOnCompetitor]) => (
                  <tr key={feature}>
                    <td className="feature_column">
                      {withSmartling(`mktg_competitor_item_${kebabCase(feature)}`) || feature}
                    </td>
                    <td
                      css={theme => ({
                        background: isOnLucid ? theme.colors.blue[5] : '',
                        borderLeft: `1px solid ${theme.colors.coolGrey[100]}`,
                      })}
                    >
                      <AccessibleDescription
                        isForScreenReaderOnly
                        SRText={withSmartling(`mktg_competitor_feature_${isOnLucid ? '' : 'not-'}included`)}
                      />
                      <Icon
                        aria-hidden="true"
                        css={theme => ({
                          color: isOnLucid ? theme.colors.blue[60] : theme.colors.coolGrey[50],
                          width: isOnLucid ? '20px' : '14px',
                        })}
                        type={isOnLucid ? 'Checkmark' : 'X'}
                      />
                    </td>
                    <td css={theme => ({ background: isOnCompetitor ? theme.colors.blue[5] : '' })}>
                      <AccessibleDescription
                        isForScreenReaderOnly
                        SRText={withSmartling(`mktg_competitor_feature_${isOnCompetitor ? '' : 'not-'}included`)}
                      />
                      <Icon
                        aria-hidden="true"
                        css={theme => ({
                          color: isOnCompetitor ? theme.colors.blue[60] : theme.colors.coolGrey[50],
                          width: isOnCompetitor ? '20px' : '14px',
                        })}
                        type={isOnCompetitor ? 'Checkmark' : 'X'}
                      />
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Paper>
    </Container>
  );
};

export default ComparisonTable;

export { ComparisonTable };
